import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../contexts/AppContext.jsx';
import { appConfig } from '../lib/config.js';
import { supabase } from '../lib/supabase.js';

const baseLimit = 5;
const maxLimit = 15;

const steps = [
  { id: 1, title: 'Share your link' },
  { id: 2, title: 'Friend joins' },
  { id: 3, title: 'You get +2 daily applications' },
];

const statusStyles = {
  pending: 'bg-slate-100 text-slate-500',
  signed_up: 'bg-amber-50 text-amber-700',
  active: 'bg-emerald-50 text-emerald-700',
};

const statusLabels = {
  pending: 'Pending',
  signed_up: 'Signed up',
  active: 'Active',
};

export default function ReferralsPage() {
  const { interviewee, loading: appLoading, refreshInterviewee } = useApp();
  const [copyLabel, setCopyLabel] = useState('Copy');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function fetchReferrals() {
      if (!interviewee?.id) return;
      setLoading(true);
      setError('');
      try {
        const { data, error: fetchError } = await supabase
          .from('career_referrals')
          .select('id, status, created_at, referred_whatsapp, referred_id')
          .eq('referrer_id', interviewee.id)
          .order('created_at', { ascending: false });
        if (fetchError) throw fetchError;
        if (!cancelled) setReferrals(data || []);
      } catch (err) {
        console.error('ReferralsPage fetch error', err);
        if (!cancelled) setError(err?.message || 'Could not load referrals.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchReferrals();
    return () => {
      cancelled = true;
    };
  }, [interviewee?.id]);

  const referralLink = useMemo(() => {
    if (!interviewee?.referral_code || !appConfig.landingUrl) return '';
    return `${appConfig.landingUrl}/join/${interviewee.referral_code}`;
  }, [interviewee?.referral_code]);

  const dailyLimit = Number(interviewee?.daily_apply_limit || baseLimit);
  const bonusLimit = Math.max(dailyLimit - baseLimit, 0);
  const progress = Math.min((dailyLimit / maxLimit) * 100, 100);

  async function ensureReferralCode() {
    if (interviewee?.referral_code) return interviewee.referral_code;
    setError('Reply REFERRAL in WhatsApp or iMessage to generate your first referral code.');
    return '';
  }

  const handleCopy = async () => {
    const code = await ensureReferralCode();
    if (!code || !referralLink || !navigator.clipboard) return;
    await navigator.clipboard.writeText(referralLink);
    setCopyLabel('Copied');
    window.setTimeout(() => setCopyLabel('Copy'), 1600);
  };

  const handleShare = async () => {
    const code = await ensureReferralCode();
    if (!code || !referralLink || !navigator.share) return;
    await navigator.share({
      title: 'Join Pareto Career Agency',
      text: 'Get job matches and paid interview opportunities through Pareto.',
      url: referralLink,
    });
  };

  if (appLoading || loading) return <ReferralsSkeleton />;

  return (
    <section className="space-y-6">
      {error ? <div className="rounded-xl border border-coral-200 bg-coral-50 px-4 py-3 text-sm font-bold text-coral-700">{error}</div> : null}
      <HeroBanner />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <main className="space-y-6">
          <ReferralLinkCard referralLink={referralLink} copyLabel={copyLabel} onCopy={handleCopy} onShare={handleShare} onRefresh={refreshInterviewee} />
          <ReferralSteps />
          <ReferredUsers users={referrals} />
        </main>
        <aside className="space-y-6">
          <BonusStatus dailyLimit={dailyLimit} bonusLimit={bonusLimit} progress={progress} />
          <TermsCard />
        </aside>
      </div>
    </section>
  );
}

function HeroBanner() {
  return (
    <header className="rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 p-6 text-white shadow-md">
      <h1 className="font-display text-3xl text-white">Get more daily applications.</h1>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-white/85">Every friend who signs up gives you +2 applications per day.</p>
    </header>
  );
}

function ReferralLinkCard({ referralLink, copyLabel, onCopy, onShare, onRefresh }) {
  return (
    <section className="rounded-xl bg-white p-4 shadow-sm shadow-slate-900/5 sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Referral link</p>
          <h2 className="mt-1 text-xl font-bold text-obsidian-400">Invite friends to Pareto</h2>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <button type="button" onClick={onCopy} disabled={!referralLink} className="h-11 cursor-pointer rounded-[8px] bg-obsidian-400 px-4 text-sm font-bold text-white shadow-lg shadow-slate-900/10 transition hover:-translate-y-0.5 hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-50">
            {copyLabel}
          </button>
          <button type="button" onClick={onShare} disabled={!referralLink} className="h-11 cursor-pointer rounded-[8px] bg-teal-600 px-4 text-sm font-bold text-white shadow-lg shadow-teal-900/10 transition hover:-translate-y-0.5 hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-50">
            Share
          </button>
        </div>
      </div>
      <div className="mt-5 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
        <p className="break-all text-base font-black text-obsidian-400">{referralLink || 'Referral link not generated yet'}</p>
        {!referralLink ? (
          <button type="button" onClick={onRefresh} className="mt-3 text-sm font-bold text-teal-700 underline decoration-teal-200 underline-offset-4">
            Refresh after generating in messaging
          </button>
        ) : null}
      </div>
    </section>
  );
}

function ReferralSteps() {
  return (
    <section className="rounded-xl bg-white p-4 shadow-sm shadow-slate-900/5 sm:p-5">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">How it works</p>
      <div className="mt-5 grid gap-4 md:grid-cols-3">
        {steps.map((step, index) => (
          <div key={step.id} className="relative flex gap-3 md:block">
            {index < steps.length - 1 ? <div className="absolute left-5 top-11 h-[calc(100%+1rem)] w-px bg-teal-100 md:left-[calc(50%+1.25rem)] md:top-5 md:h-px md:w-[calc(100%-2.5rem)]" /> : null}
            <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-black text-white shadow-lg shadow-teal-900/10 md:mx-auto">
              {step.id}
            </div>
            <div className="rounded-xl bg-slate-50 p-4 md:mt-4 md:text-center">
              <h3 className="text-base font-bold text-obsidian-400">{step.title}</h3>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BonusStatus({ dailyLimit, bonusLimit, progress }) {
  return (
    <section className="rounded-xl bg-white p-5 shadow-sm shadow-slate-900/5">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Current bonus status</p>
      <p className="mt-3 text-2xl font-black leading-tight text-obsidian-400">
        You have {baseLimit} base + {bonusLimit} bonus = {dailyLimit} daily applications
      </p>
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
          <span>{dailyLimit} today</span>
          <span>{maxLimit} max</span>
        </div>
        <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-3 text-sm font-semibold leading-5 text-slate-500">{Math.max(maxLimit - dailyLimit, 0)} more daily applications available before the cap.</p>
      </div>
    </section>
  );
}

function ReferredUsers({ users }) {
  return (
    <section className="space-y-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Referred users</p>
        <h2 className="mt-1 text-2xl font-bold text-obsidian-400">Invite activity</h2>
      </div>
      {users.length === 0 ? (
        <div className="rounded-xl bg-white p-6 text-center shadow-sm shadow-slate-900/5">
          <p className="text-sm font-semibold text-slate-500">No referrals yet. Share your link to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <article key={user.id} className="rounded-xl bg-white p-4 shadow-sm shadow-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-lg">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-bold text-obsidian-400">{user.referred_whatsapp || 'Referred participant'}</h3>
                  <p className="mt-1 text-sm font-semibold text-slate-500">{formatDate(user.created_at)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${statusStyles[user.status] || statusStyles.pending}`}>{statusLabels[user.status] || user.status}</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function TermsCard() {
  return (
    <section className="rounded-xl bg-slate-100 p-5 shadow-sm shadow-slate-900/5">
      <a href="/terms" className="text-sm font-bold text-teal-700 underline decoration-teal-200 underline-offset-4 hover:text-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-100">
        Terms and Conditions apply
      </a>
    </section>
  );
}

function ReferralsSkeleton() {
  return (
    <section className="space-y-6 animate-pulse">
      <div className="h-36 rounded-2xl bg-slate-200" />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <div className="h-44 rounded-xl bg-slate-200" />
          <div className="h-48 rounded-xl bg-slate-200" />
        </div>
        <div className="h-56 rounded-xl bg-slate-200" />
      </div>
    </section>
  );
}

function formatDate(value) {
  if (!value) return 'Date pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date pending';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
